import type { Parameters, ScenarioId, System, Zone } from "../simulation/types";
export const ZONES: Zone[] = [
  {
    id: "beacon",
    name: "Beacon Park",
    short: "Beacon",
    coordinates: [-1.838, 52.6844],
    households: 620,
    floodExposure: 1.2,
    transportExposure: 0.5,
    powerExposure: 0.65,
    type: "Green space & west neighbourhood",
    description:
      "The city’s western green space. Explore the mapped park, then test a fictional drainage response across the surrounding neighbourhood.",
  },
  {
    id: "cathedral",
    name: "Cathedral Quarter",
    short: "Cathedral",
    coordinates: [-1.8305, 52.6858],
    households: 460,
    floodExposure: 1.1,
    transportExposure: 0.65,
    powerExposure: 0.9,
    type: "Heritage & waterside",
    description:
      "Lichfield Cathedral and Minster Pool anchor the historic centre. The footprint is mapped; spire heights and all service vulnerabilities are illustrative.",
  },
  {
    id: "stowe",
    name: "Stowe & the pools",
    short: "Stowe",
    coordinates: [-1.8227, 52.6884],
    households: 1140,
    floodExposure: 1.4,
    transportExposure: 0.65,
    powerExposure: 0.8,
    type: "Waterside & neighbourhood",
    description:
      "Stowe Pool is part of Lichfield’s distinctive landscape. The simulation assigns this area a higher fictional exposure to heavy rainfall.",
  },
  {
    id: "centre",
    name: "City Centre",
    short: "Centre",
    coordinates: [-1.8265, 52.6834],
    households: 820,
    floodExposure: 0.8,
    transportExposure: 1.1,
    powerExposure: 1.25,
    type: "Civic & commercial",
    description:
      "The streets around Market Square connect the city’s daily activity. Fictional demand and traffic dependencies make this a useful place to explore cascading effects.",
  },
  {
    id: "city",
    name: "Lichfield City Station",
    short: "City station",
    coordinates: [-1.8251247, 52.6802201],
    households: 1280,
    floodExposure: 0.6,
    transportExposure: 1.35,
    powerExposure: 1,
    type: "Rail & south neighbourhood",
    description:
      "A real rail station on the Cross-City route, with a mapped rail corridor. Service frequency, passengers and disruptions in PULSE are entirely simulated.",
  },
  {
    id: "trent",
    name: "Trent Valley Gateway",
    short: "Trent Valley",
    coordinates: [-1.8006, 52.6868],
    households: 980,
    floodExposure: 0.5,
    transportExposure: 1.6,
    powerExposure: 1.1,
    type: "Rail & eastern gateway",
    description:
      "The eastern rail gateway is a real place. The fictional scenario models how disruption here can put pressure on the city’s other transport connections.",
  },
];
export const ZONE_BY_ID = Object.fromEntries(
  ZONES.map((z) => [z.id, z]),
) as Record<Zone["id"], Zone>;
export const SCENARIOS: Record<
  ScenarioId,
  {
    name: string;
    label: string;
    description: string;
    parameters: Parameters;
    accent: string;
  }
> = {
  calm: {
    name: "A city in balance",
    label: "Everyday Lichfield",
    description:
      "Begin with a calm city. Explore its places and discover the connections beneath everyday life.",
    parameters: { rain: 0, wind: 8, disruption: 0, demand: 100 },
    accent: "#c7ed94",
  },
  storm: {
    name: "When the rain arrives",
    label: "Severe weather",
    description:
      "Intense rainfall and high winds strain drainage. Power stress spills into transport. Where will you send the first crew?",
    parameters: { rain: 44, wind: 72, disruption: 18, demand: 122 },
    accent: "#9fd9ed",
  },
  rail: {
    name: "A gateway interrupted",
    label: "Transport disruption",
    description:
      "A major fictional rail interruption at Trent Valley sends pressure through the city’s mobility network.",
    parameters: { rain: 0, wind: 8, disruption: 88, demand: 108 },
    accent: "#edb477",
  },
  energy: {
    name: "Demand beyond capacity",
    label: "Energy stress",
    description:
      "A sharp increase in local electricity demand tests continuity. Transport begins to feel the consequences.",
    parameters: { rain: 4, wind: 18, disruption: 12, demand: 174 },
    accent: "#d0b8f4",
  },
};
export const SYSTEM_INFO: Record<
  System,
  { label: string; crew: string; colour: string; unit: string }
> = {
  water: {
    label: "Drainage",
    crew: "Drainage crew",
    colour: "#91c8df",
    unit: "water stress",
  },
  transport: {
    label: "Mobility",
    crew: "Transport crew",
    colour: "#e6b37e",
    unit: "transport stress",
  },
  power: {
    label: "Energy",
    crew: "Power crew",
    colour: "#cbb5ef",
    unit: "energy stress",
  },
};
